/**
 * Search TanStack Query Hooks (T158)
 * Data fetching with debounced search input
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import apiClient, { type ApiResponse } from '../lib/api-client';

// Types

export interface CustomerResult {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  tags: string[];
  matchField: string;
  relevance: number;
}

export interface AppointmentResult {
  id: string;
  customerName: string;
  title: string;
  startTime: string;
  status: string;
  snippet: string;
  relevance: number;
}

export interface InvoiceResult {
  id: string;
  invoiceNumber: string;
  customerName: string;
  total: number;
  status: string;
  dueDate: string;
  relevance: number;
}

export interface RecordingResult {
  recordingId: string;
  appointmentId: string;
  customerName: string;
  appointmentDate: string;
  snippet: string;
  relevance: number;
}

export interface SearchResults {
  query: string;
  totalResults: number;
  customers: { count: number; results: CustomerResult[] };
  appointments: { count: number; results: AppointmentResult[] };
  invoices: { count: number; results: InvoiceResult[] };
  recordings: { count: number; results: RecordingResult[] };
}

export interface SearchParams {
  query: string;
  types?: string;
  limit?: number;
  page?: number;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// Query hooks

export function useSearch(params: SearchParams) {
  const debouncedQuery = useDebounce(params.query, 300);

  return useQuery({
    queryKey: ['search', debouncedQuery, params.types, params.limit, params.page],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.append('q', debouncedQuery);
      if (params.types) {
        searchParams.append('types', params.types);
      }
      if (params.limit) {
        searchParams.append('limit', params.limit.toString());
      }
      if (params.page) {
        searchParams.append('page', params.page.toString());
      }

      const response = await apiClient.get<ApiResponse<SearchResults>>(
        `/search?${searchParams.toString()}`
      );
      return response.data.data;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });
}

export function useQuickSearch(query: string) {
  const debouncedQuery = useDebounce(query, 300);

  return useQuery({
    queryKey: ['search', 'quick', debouncedQuery],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<SearchResults>>(
        `/search?q=${encodeURIComponent(debouncedQuery)}&limit=3`
      );
      return response.data.data;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 15_000,
  });
}

export { useDebounce };
